<template>
    <a-modal
        :visible="true"
        :width="'70vw'"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="spo-wrap"
        @cancel="$emit('close')"
    >
        <div class="spo-modal" v-if="provider">
            <div class="spo-head">
                <div>
                    <h3>Pré-pedido do vendedor</h3>
                    <div class="spo-sub">
                        {{ provider.nomeFornecedor }}<template v-if="provider.cnpj"> · {{ provider.cnpj }}</template>
                        <template v-if="provider.nomeVendedor"> · {{ provider.nomeVendedor }}</template>
                    </div>
                    <div class="spo-meta">
                        <span>Cond. Pgto.: {{ provider.paymentCondition || "—" }}</span>
                        <span>Entrega prevista: {{ provider.expectedDate ? $options.filters.formatDateOnly(provider.expectedDate) : "—" }}</span>
                        <span>Tipo de entrega: {{ provider.deliveryType || "—" }}</span>
                        <span v-if="provider.minimumBilling">Fat. mín.: {{ provider.minimumBilling | currency }}</span>
                    </div>
                </div>

                <div class="spo-head-right">
                    <div class="spo-store-select" v-if="involvedStores.length > 1">
                        <a-icon type="shop" class="spo-store-icon" />
                        <a-select
                            v-model="activeStoreId"
                            class="spo-store-select-input"
                            show-search
                            option-filter-prop="children"
                            :dropdown-match-select-width="false"
                            :dropdown-style="{ minWidth: '300px' }"
                            placeholder="Selecione a loja"
                        >
                            <a-select-option
                                v-for="store in involvedStores"
                                :key="store.id"
                                :value="store.id"
                            >
                                {{ store.name }}<template v-if="store.trading_name && store.trading_name !== store.name"> - {{ store.trading_name }}</template>
                            </a-select-option>
                        </a-select>
                    </div>

                    <a-tooltip title="Fechar">
                        <a-button class="spo-x" @click="$emit('close')">
                            <span class="material-icons-outlined">close</span>
                        </a-button>
                    </a-tooltip>
                </div>
            </div>

            <div class="spo-info" v-if="activeStore">
                <div class="spo-info-col">
                    <div class="spo-info-title">Comprador</div>
                    <div class="spo-info-name">
                        {{ activeStore.name }}<template v-if="activeStore.trading_name && activeStore.trading_name !== activeStore.name"> - {{ activeStore.trading_name }}</template>
                    </div>
                    <div class="spo-info-line" v-if="activeStore.street">
                        {{ activeStore.street }}<template v-if="activeStore.district"> — {{ activeStore.district }}</template><template v-if="activeStore.city && activeStore.city.name"> — {{ activeStore.city.name }}<template v-if="activeStore.state && activeStore.state.name">/{{ activeStore.state.name }}</template></template>
                    </div>
                    <div class="spo-info-line" v-if="activeStore.cnpj">CNPJ: {{ activeStore.cnpj | cnpj }}</div>
                    <div class="spo-info-line" v-if="activeStore.phone">
                        <a :href="'https://api.whatsapp.com/send?phone=' + formatWhatsappNumber(activeStore.phone)" target="_blank">{{ activeStore.phone }}</a>
                    </div>
                </div>

                <div class="spo-info-col">
                    <div class="spo-info-title">Fornecedor / Vendedor</div>
                    <div class="spo-info-name">
                        {{ provider.nomeFornecedor }}
                        <span class="spo-rating" v-if="currentSubrequest && currentSubrequest.rate != null">
                            <a-icon type="star" theme="filled" /> {{ Number(currentSubrequest.rate).toFixed(1) }}
                        </span>
                    </div>
                    <div class="spo-info-line" v-if="provider.nomeVendedor">
                        {{ provider.nomeVendedor }}<template v-if="provider.telefone"> · <a :href="'https://api.whatsapp.com/send?phone=' + formatWhatsappNumber(provider.telefone)" target="_blank">{{ provider.telefone }}</a></template>
                    </div>
                    <div class="spo-info-line" v-if="currentSubrequest && currentSubrequest.usu_email">{{ currentSubrequest.usu_email }}</div>
                </div>

                <div class="spo-info-col">
                    <div class="spo-info-title">Pedido</div>
                    <div class="spo-info-line">
                        Status:
                        <span v-if="currentSubrequest && currentSubrequest.id_pedido" class="spo-status spo-status-done">gerado</span>
                        <span v-else class="spo-status spo-status-pending">pendente</span>
                    </div>
                    <div class="spo-info-line" v-if="currentSubrequest && currentSubrequest.id_pedido">
                        Nº do pedido: {{ currentSubrequest.id_pedido }}
                    </div>
                    <div class="spo-info-line" v-if="saldo < 0">
                        Saldo: <b class="spo-saldo-neg">{{ saldo | currency }}</b>
                    </div>
                </div>
            </div>

            <div class="spo-obs" v-if="currentSubrequest">
                <div class="spo-obs-head">
                    <span>Observação</span>
                    <a-icon type="save" class="spo-obs-save" :class="{ saving: savingObs }" @click.native="saveObservacao" />
                </div>
                <a-textarea
                    v-if="isTextObs"
                    class="spo-obs-textarea"
                    v-model="currentSubrequest.obs"
                    placeholder="Digite aqui se houver alguma observação"
                    :rows="2"
                />
                <div v-else class="spo-obs-grid">
                    <a-input v-model="obsForm.eventNumber" size="small" placeholder="Nº do evento" />
                    <a-input v-model="obsForm.car" size="small" placeholder="Modelo do carro" />
                    <a-input v-model="obsForm.address" size="small" placeholder="Endereço de entrega" />
                    <a-input v-model="obsForm.pix" size="small" placeholder="Nº do PIX" />
                </div>
            </div>

            <div class="spo-body">
                <a-table
                    size="small"
                    :columns="columns"
                    :data-source="activeProducts"
                    row-key="id"
                    :pagination="false"
                    :locale="{ emptyText: 'Nenhum produto para esta loja.' }"
                >
                    <template slot="produto" slot-scope="text, product">
                        <div class="pro-name">
                            {{ product.name }}
                            <span class="brand" v-if="product.brand">{{ product.brand }}</span>
                        </div>
                    </template>

                    <template slot="embalagem" slot-scope="text, product">
                        {{ product.purchase_unity || `${product.packing.split(" ")[0]} ${product.packing.split(" ")[1]}` }}
                    </template>

                    <template slot="ressuprimento" slot-scope="text, product">
                        <span class="repl-tag" v-if="replenishment(product)">{{ replenishment(product) }}</span>
                        <span v-else>--</span>
                    </template>

                    <template slot="preco" slot-scope="text, product">{{ product.offer_price | currency }}</template>

                    <template slot="last_purchase" slot-scope="text, product">{{ product.last_purchase | currency }}</template>

                    <template slot="diferenca" slot-scope="text, product">
                        <span v-if="product.last_purchase" :class="deltaClass(product)">
                            {{ (product.offer_price - product.last_purchase) | currency }}
                        </span>
                        <span v-else>—</span>
                    </template>

                    <template slot="variacao" slot-scope="text, product">
                        <span v-if="product.last_purchase" :class="deltaClass(product)">{{ variation(product).toFixed(1) }}%</span>
                        <span v-else>—</span>
                    </template>

                    <template slot="req" slot-scope="text, product">{{ storeEntry(product).request_quantity || 0 }}</template>

                    <template slot="qtd" slot-scope="text, product">
                        <input
                            v-if="isActiveCell(product)"
                            ref="activeInput"
                            type="text"
                            class="spo-qty-input ant-input"
                            v-model="activeValue"
                            @input="handleQtyInput($event, product)"
                            @blur="commitCell(product)"
                            @keydown="handleKeydown($event, product)"
                        />
                        <span
                            v-else
                            class="spo-qty-input ant-input spo-qty-display"
                            @click="activateCell(product)"
                        >{{ displayQuantity(product) }}</span>
                        <div class="spo-qty-caption" v-if="storeEntry(product).inventory != null">
                            Estoque: {{ storeEntry(product).inventory | stock }}
                        </div>
                    </template>

                    <template slot="total" slot-scope="text, product">{{ lineTotal(product) | currency }}</template>
                </a-table>
            </div>

            <div class="spo-foot">
                <div class="spo-total">Valor total: <b>{{ activeTotal | currency }}</b></div>
                <div class="spo-foot-actions">
                    <a-button v-if="currentSubrequest && currentSubrequest.id_pedido" :loading="printing" @click="printOrder">Imprimir</a-button>
                    <a-button
                        v-if="currentSubrequest && currentSubrequest.id_pedido"
                        type="danger"
                        :loading="cancelling"
                        @click="cancelOrder"
                    >Cancelar pedido</a-button>
                    <a-button v-else type="primary" :disabled="generating" @click="generateOrder">Realizar pedido</a-button>
                </div>
            </div>
        </div>
    </a-modal>
</template>

<script>
import QuotationService from "@/services/QuotationService";
import RequestsService from "@/services/v2/requests.service";
import V3OrderService from "@/services/v3/order.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import { replenishmentLabel } from "@/helpers/replenishment";
import { mapState } from "vuex";

export default {
    name: "SellerPreOrderModal",
    mixins: [loaderMixin],
    props: {
        provider: { type: Object, required: true },
        stores: { type: Array, default: () => [] },
        quotationId: [String, Number],
        generating: { type: Boolean, default: false },
    },
    data() {
        return {
            svc: new QuotationService(),
            requestsSvc: new RequestsService(),
            orderSvc: new V3OrderService(),
            originalQuantities: {},
            activeCell: null,
            activeValue: "",
            activeStoreId: null,
            allSubrequests: [],
            isTextObs: true,
            obsForm: { eventNumber: "", car: "", address: "", pix: "" },
            savingObs: false,
            cancelling: false,
            printing: false,
        };
    },
    computed: {
        ...mapState(["user"]),
        canCancelOrder() {
            const user = this.user || {};
            return !user.perms?.some(
                (perm) =>
                    perm.resource === "REQUEST_ORDER" &&
                    perm.action === "CANCEL" &&
                    perm.subject_ids === null
            );
        },
        currentSubrequest() {
            return (
                this.allSubrequests.find(
                    (s) =>
                        s.loj_id == this.activeStoreId &&
                        s.vendedor_id == this.provider.seller_id &&
                        s.for_id == this.provider.provider_id
                ) || null
            );
        },
        saldo() {
            const min = this.provider.minimumBilling || 0;
            return min > this.activeTotal ? this.activeTotal - min : 0;
        },
        involvedStores() {
            const ids = new Set();

            this.provider.products.forEach((product) => {
                product.stores.forEach((store) => {
                    if (Number(store.requested_quantity) > 0) ids.add(store.store_id);
                });
            });

            return this.stores.filter((store) => ids.has(store.id));
        },
        activeStore() {
            return this.stores.find((s) => s.id === this.activeStoreId) || null;
        },
        // Only what this loja actually has pré-pedido lines for — not a
        // blank matrix of every product padded to 0.
        activeProducts() {
            return this.provider.products.filter((p) => Number(this.storeEntry(p).requested_quantity) > 0);
        },
        columns() {
            return [
                { title: "Produto", key: "produto", scopedSlots: { customRender: "produto" } },
                { title: "Embalagem", key: "embalagem", width: 90, align: "center", scopedSlots: { customRender: "embalagem" } },
                { title: "Reabastecimento", key: "ressuprimento", width: 120, align: "center", scopedSlots: { customRender: "ressuprimento" } },
                { title: "Preço", key: "preco", width: 90, align: "right", scopedSlots: { customRender: "preco" } },
                { title: "Últ. Compra", key: "last_purchase", width: 90, align: "right", scopedSlots: { customRender: "last_purchase" } },
                { title: "Diferença", key: "diferenca", width: 90, align: "right", scopedSlots: { customRender: "diferenca" } },
                { title: "Variação", key: "variacao", width: 80, align: "right", scopedSlots: { customRender: "variacao" } },
                { title: "Req.", key: "req", width: 70, align: "center", scopedSlots: { customRender: "req" } },
                { title: "Qtd.", key: "qtd", width: 90, align: "center", scopedSlots: { customRender: "qtd" } },
                { title: "Total", key: "total", width: 100, align: "right", scopedSlots: { customRender: "total" } },
            ];
        },
        activeTotal() {
            return this.provider.products.reduce((sum, product) => sum + this.lineTotal(product), 0);
        },
    },
    created() {
        this.activeStoreId = this.involvedStores.length ? this.involvedStores[0].id : null;
        this.loadSubrequests();
    },
    watch: {
        currentSubrequest: {
            immediate: true,
            handler(sub) {
                this.parseObs(sub);
            },
        },
    },
    methods: {
        loadSubrequests() {
            this.requestsSvc
                .subrequests_with_sellers_for(this.quotationId)
                .then((response) => {
                    this.allSubrequests = response.data;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        parseObs(sub) {
            if (!sub || !sub.obs) {
                this.isTextObs = true;
                this.obsForm = { eventNumber: "", car: "", address: "", pix: "" };
                return;
            }

            try {
                const form = JSON.parse(sub.obs);
                this.isTextObs = false;
                this.obsForm = {
                    eventNumber: form.eventNumber || "",
                    car: form.car || "",
                    address: form.address || "",
                    pix: form.pix || "",
                };
            } catch {
                this.isTextObs = true;
            }
        },
        saveObservacao() {
            if (!this.currentSubrequest) return;

            const obs = this.isTextObs ? this.currentSubrequest.obs : JSON.stringify(this.obsForm);

            this.savingObs = true;

            this.requestsSvc
                .save_pre_request_details(this.quotationId, {
                    obs,
                    loj_id: this.currentSubrequest.loj_id,
                    usu_id: this.currentSubrequest.vendedor_id,
                })
                .then(() => {
                    this.snack("Observação salva.");
                })
                .catch((error) => {
                    this.snack("Não foi possível salvar a observação.", "error");
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.savingObs = false;
                });
        },
        formatWhatsappNumber(number) {
            if (!number) return null;

            let clean = String(number).replace(/\D/g, "");

            if (clean.slice(0, 2) !== "55") clean = "55" + clean;

            return clean;
        },
        cancelOrder() {
            if (!this.currentSubrequest || !this.currentSubrequest.id_pedido) return;

            if (!this.canCancelOrder) {
                this.snack("Você não tem permissão para cancelar pedidos.", "error");
                return;
            }

            this.$swal
                .fire({
                    title: "Tem certeza que deseja cancelar este pedido?",
                    icon: "warning",
                    input: "select",
                    inputOptions: {
                        1: "Quantidade Errada",
                        2: "Valores Divergentes",
                        3: "Ruptura de Produtos",
                        4: "Entrega não realizada",
                        5: "Outros",
                    },
                    inputPlaceholder: "Selecione um motivo",
                    showCancelButton: true,
                    confirmButtonColor: "#d33",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: "Sim, cancelar",
                    cancelButtonText: "Não",
                    inputValidator: (value) => {
                        if (!value) return "Você precisa escolher um motivo!";
                    },
                })
                .then(({ value: motivoId, isConfirmed }) => {
                    if (!isConfirmed) return;

                    this.cancelling = true;

                    this.orderSvc
                        .cancel(this.currentSubrequest.id_pedido, motivoId)
                        .then(() => {
                            this.$swal.fire("Sucesso!", "Pedido cancelado com sucesso!", "success");
                            this.loadSubrequests();
                            this.$emit("reload");
                        })
                        .catch((error) => {
                            ErrorHandlerService.handle(error, this.$store);
                        })
                        .finally(() => {
                            this.cancelling = false;
                        });
                });
        },
        printOrder() {
            if (!this.currentSubrequest || !this.currentSubrequest.id_pedido) return;

            this.printing = true;

            this.orderSvc
                .export_pdf([this.currentSubrequest.id_pedido])
                .then((response) => {
                    const file = new Blob([response.data], { type: "application/pdf" });
                    const fileURL = URL.createObjectURL(file);
                    const link = document.createElement("a");

                    link.href = fileURL;
                    link.download = `Pedido-${this.currentSubrequest.id_pedido}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => URL.revokeObjectURL(fileURL), 100);
                })
                .catch((error) => {
                    this.snack("Não foi possível gerar o PDF.", "error");
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.printing = false;
                });
        },
        replenishment(product) {
            return replenishmentLabel(product.replenishment_strategy);
        },
        deltaClass(product) {
            if (product.offer_price > product.last_purchase) return "vpm-up";
            if (product.offer_price < product.last_purchase) return "vpm-down";
            return "";
        },
        variation(product) {
            if (!product.last_purchase) return 0;
            return ((product.offer_price - product.last_purchase) / product.last_purchase) * 100;
        },
        storeEntry(product) {
            return (product.stores || []).find((s) => s.store_id == this.activeStoreId) || {};
        },
        displayQuantity(product) {
            return this.storeEntry(product).requested_quantity || 0;
        },
        lineTotal(product) {
            const entry = this.storeEntry(product);
            const quantity = Number(entry.requested_quantity) || 0;
            const unitValue = product.is_generic == 0 && product.main_product_id == null
                ? parseInt(product.packing.split(" ")[1]) * product.offer_price
                : product.offer_price;

            return quantity * unitValue;
        },
        isActiveCell(product) {
            return this.activeCell === product.id;
        },
        activateCell(product) {
            const current = this.storeEntry(product);

            this.originalQuantities[product.id] = Number(current.requested_quantity || 0);
            this.activeValue = String(current.requested_quantity || "");
            this.activeCell = product.id;

            this.$nextTick(() => {
                const refs = this.$refs.activeInput;
                const el = Array.isArray(refs) ? refs[0] : refs;

                if (el) {
                    el.focus();
                    el.select();
                }
            });
        },
        replaceStoreEntry(product, changes) {
            const index = product.stores.findIndex((s) => s.store_id == this.activeStoreId);

            if (index === -1) return;

            const updated = Object.freeze({ ...product.stores[index], ...changes });

            product.stores.splice(index, 1, updated);
        },
        handleQtyInput(event, product) {
            let value = event.target.value.replace(/\D/g, "");
            value = value.replace(/^0+(?=\d)/, "");

            this.activeValue = value;
            event.target.value = value;

            this.replaceStoreEntry(product, { requested_quantity: value === "" ? 0 : Number(value) });
        },
        handleKeydown(event, product) {
            if (!["Enter", "ArrowDown", "ArrowUp"].includes(event.key)) return;

            event.preventDefault();

            const rows = this.provider.products;
            const rowIndex = rows.findIndex((p) => p.id === product.id);

            if (rowIndex === -1) return;

            let nextRow = rowIndex;

            switch (event.key) {
                case "Enter":
                case "ArrowDown":
                    nextRow = rowIndex + 1;
                    break;

                case "ArrowUp":
                    nextRow = rowIndex - 1;
                    break;
            }

            nextRow = Math.max(0, Math.min(nextRow, rows.length - 1));

            const nextProduct = rows[nextRow];

            if (nextProduct) this.activateCell(nextProduct);
        },
        commitCell(product) {
            this.saveQuantity(product);

            if (this.activeCell === product.id) this.activeCell = null;
        },
        saveQuantity(product) {
            const entry = this.storeEntry(product);
            const quantidade = Number(entry.requested_quantity) || 0;
            const oldValue = this.originalQuantities[product.id];

            if (quantidade === oldValue) return;

            this.originalQuantities[product.id] = quantidade;

            this.svc
                .sendQuantidade({
                    numerocotacao: this.quotationId,
                    quantidade,
                    vendedor: product.seller_id,
                    produto: product.id,
                    loja: this.activeStoreId,
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        generateOrder() {
            if (!this.activeProducts.length) {
                this.snack("Nenhuma quantidade informada para gerar pedido.", "error");
                return;
            }

            const keys = this.activeProducts.map((p) => {
                const entry = this.storeEntry(p);
                return `${entry.id}-${entry.store_id}-${entry.provider_id}-${entry.seller_id}`;
            });

            this.confirm_action({
                message: `Gerar pedido para ${this.provider.nomeFornecedor} em ${this.activeStore ? this.activeStore.name : "1 loja"}?`,
                confirm_text: "Gerar pedido",
                callback: () => {
                    this.$emit("generate-orders", keys);
                    // Closes right away rather than waiting for the
                    // generate-orders call to resolve: the modal has an
                    // a-modal mask at the same z-index as the global
                    // notification container, so leaving it open hid the
                    // "N pedido(s) gerado(s)" feedback behind it once the
                    // call succeeded.
                    this.$emit("close");
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.spo-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.spo-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
        margin: 0;
    }
}
.spo-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.spo-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    margin-top: 8px;
    font-size: 12px;
    color: #595959;
}
.spo-head-right {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex-shrink: 0;
}
.spo-x.ant-btn,
.spo-x.ant-btn:focus,
.spo-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.spo-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.spo-store-select {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    width: 360px;
    background: #fafafa;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
}
.spo-store-icon {
    color: #8c8c8c;
    font-size: 14px;
    flex-shrink: 0;
}
.spo-store-select-input {
    flex: 1;
    min-width: 0;
}
.spo-store-select-input ::v-deep .ant-select-selection {
    border: none;
    background: transparent;
    box-shadow: none;
}
.spo-info {
    display: flex;
    gap: 24px;
    padding: 0 20px 14px;
    flex-wrap: wrap;
}
.spo-info-col {
    flex: 1 1 200px;
    min-width: 180px;
}
.spo-info-title {
    font-size: 11px;
    font-weight: 600;
    color: #8c8c8c;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-bottom: 4px;
}
.spo-info-name {
    font-size: 13px;
    font-weight: 600;
    color: #262626;
}
.spo-info-line {
    font-size: 12px;
    color: #595959;
    margin-top: 2px;

    a {
        color: #595959;
        text-decoration: underline;
    }
}
.spo-rating {
    font-size: 12px;
    color: #faad14;
    font-weight: 600;
    margin-left: 4px;
}
.spo-status {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
}
.spo-status-pending {
    background: #fff7e6;
    color: #d48806;
}
.spo-status-done {
    background: #f6ffed;
    color: #389e0d;
}
.spo-saldo-neg {
    color: #cf1322;
}
.spo-obs {
    padding: 0 20px 14px;
}
.spo-obs-head {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 600;
    color: #8c8c8c;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-bottom: 4px;
}
.spo-obs-save {
    cursor: pointer;
    color: #8c8c8c;

    &:hover {
        color: var(--primary-color, #1890ff);
    }

    &.saving {
        opacity: 0.5;
        pointer-events: none;
    }
}
.spo-obs-textarea {
    resize: vertical;
    font-size: 12px;
}
.spo-obs-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 12px;
}
.spo-body {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
    padding: 0 20px;
    border-top: 1px solid #f0f0f0;
}
.pro-name {
    font-weight: 600;
    color: #262626;
    font-size: 13px;
}
.brand {
    margin-left: 6px;
    color: #999;
    font-size: 12px;
    font-weight: 400;
}
.repl-tag {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    background: #f0f0f0;
    color: #595959;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.spo-qty-input {
    display: inline-block;
    box-sizing: border-box;
    width: 60px;
    text-align: center;
}
.spo-qty-display {
    cursor: text;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.spo-qty-caption {
    font-size: 11px;
    color: #8c8c8c;
    text-align: center;
    margin-top: 2px;
    white-space: nowrap;
}
.vpm-up {
    color: #cf1322;
    font-weight: 600;
}
.vpm-down {
    color: #389e0d;
    font-weight: 600;
}
.spo-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
.spo-total {
    font-size: 14px;
    color: #595959;

    b {
        font-size: 16px;
        color: #262626;
        margin-left: 6px;
    }
}
.spo-foot-actions {
    display: flex;
    gap: 8px;
}
</style>

<style lang="scss">
.spo-wrap {
    .ant-modal {
        max-width: 96vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
